import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/about.css";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const [activeTab, setActiveTab] = useState("Interior");
  const [modalImage, setModalImage] = useState(null);
  const navigate = useNavigate();
  const pageRef = useRef(null);

  const galleryData = {
    Interior: [
      { type: "image", src: "/assets/interior/1.jpeg" },
      { type: "image", src: "/assets/interior/2.jpeg" },
      { type: "image", src: "/assets/interior/3.jpeg" },
      { type: "image", src: "/assets/interior/4.jpeg" },
      { type: "image", src: "/assets/interior/5.jpeg" },
      { type: "image", src: "/assets/interior/6.jpeg" },
      { type: "image", src: "/assets/interior/7.jpeg" },
      { type: "image", src: "/assets/interior/8.jpeg" },
      { type: "image", src: "/assets/interior/9.jpeg" },
      { type: "image", src: "/assets/interior/10.jpeg" },
    ],
    "Happy Clients": [
      { type: "image", src: "/assets/happyclient/1.jpeg" },
      { type: "image", src: "/assets/happyclient/2.jpeg" },
      { type: "image", src: "/assets/happyclient/3.jpeg" },
      { type: "image", src: "/assets/happyclient/4.jpeg" },
      { type: "image", src: "/assets/happyclient/5.jpeg" },
      { type: "image", src: "/assets/happyclient/6.jpeg" },
      { type: "video", src: "/assets/happyclient/7.mp4" },
      { type: "video", src: "/assets/happyclient/8.mp4" },
      { type: "video", src: "/assets/happyclient/9.mp4" },
    ],
    Transformations: [
      { type: "image", src: "/assets/transformation/1.jpeg" },
      { type: "image", src: "/assets/transformation/2.jpeg" },
      { type: "image", src: "/assets/transformation/3.jpeg" },
      { type: "image", src: "/assets/transformation/4.jpeg" },
      { type: "image", src: "/assets/transformation/5.jpeg" },
      { type: "image", src: "/assets/transformation/6.jpeg" },
      { type: "image", src: "/assets/transformation/7.jpeg" },
      { type: "image", src: "/assets/transformation/8.jpeg" },
      { type: "image", src: "/assets/transformation/9.jpeg" },
      { type: "image", src: "/assets/transformation/10.jpeg" },
      { type: "image", src: "/assets/transformation/11.jpeg" },
      { type: "image", src: "/assets/transformation/12.jpeg" },
      { type: "video", src: "/assets/transformation/16.mp4" },
      { type: "video", src: "/assets/transformation/17.mp4" },
      { type: "video", src: "/assets/transformation/18.mp4" },
      { type: "video", src: "/assets/transformation/25.mp4" },
      { type: "video", src: "/assets/transformation/26.mp4" },
      { type: "video", src: "/assets/transformation/27.mp4" },
    ],
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".ab-hero-line", {
        yPercent: 110, opacity: 0, duration: 1.3,
        stagger: 0.18, ease: "expo.out", delay: 0.2,
      });
      gsap.from(".ab-hero-sub", { opacity: 0, y: 24, duration: 1, delay: 0.9, ease: "power3.out" });
      gsap.from(".ab-hero-cta", { opacity: 0, y: 20, duration: 1, delay: 1.1, ease: "power3.out" });

      gsap.utils.toArray(".ab-reveal").forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 87%", toggleActions: "play none none none" },
          y: 55, opacity: 0, duration: 0.9, ease: "power3.out",
        });
      });

      gsap.utils.toArray(".ab-stagger-parent").forEach((parent) => {
        const children = parent.querySelectorAll(".ab-stagger-child");
        gsap.from(children, {
          scrollTrigger: { trigger: parent, start: "top 85%", toggleActions: "play none none none" },
          y: 50, opacity: 0, duration: 0.8, stagger: 0.15, ease: "power2.out",
        });
      });

      /* stat counter */
      gsap.utils.toArray(".ab-stat-num").forEach((el) => {
        const raw = el.getAttribute("data-val");
        const isText = isNaN(raw);
        if (!isText) {
          const target = parseInt(raw);
          ScrollTrigger.create({
            trigger: el, start: "top 85%", once: true,
            onEnter: () => {
              gsap.to({ val: 0 }, {
                val: target, duration: 2, ease: "power2.out",
                onUpdate: function () {
                  el.textContent = Math.round(this.targets()[0].val) + el.getAttribute("data-suffix");
                }
              });
            }
          });
        }
      });

      /* feature rows: image slides in from left/right alternately */
      gsap.utils.toArray(".ab-feat-row").forEach((row, i) => {
        const img = row.querySelector(".ab-feat-img-wrap");
        const txt = row.querySelector(".ab-feat-text");
        gsap.from(img, {
          scrollTrigger: { trigger: row, start: "top 85%", toggleActions: "play none none none" },
          x: i % 2 === 0 ? -60 : 60, opacity: 0, duration: 1.1, ease: "expo.out",
        });
        gsap.from(txt, {
          scrollTrigger: { trigger: row, start: "top 85%", toggleActions: "play none none none" },
          x: i % 2 === 0 ? 60 : -60, opacity: 0, duration: 1.1, ease: "expo.out", delay: 0.1,
        });
      });

    }, pageRef);
    return () => ctx.revert();
  }, []);

  const stats = [
    { val: "5000", suffix: "+", label: "Happy Clients" },
    { val: "12", suffix: "+", label: "Expert Stylists" },
    { val: "8", suffix: "+", label: "Years of Artistry" },
    { val: "50", suffix: "+", label: "Services Offered" },
  ];

  const features = [
    {
      image: "/assets/aboutimg/Stylist5.webp",
      title: "Expert Stylists",
      body: "We know how important it is to trust your stylist. At King & Queen, that trust is earned through skill, patience, and true understanding. Our stylists are here to listen, guide, and bring your vision to life with a flawless finish every time.",
    },
    {
      image: "/assets/aboutimg/Bride14.webp",
      title: "One-of-a-Kind Bridal Experience",
      body: "From the moment you step in, we surround you with gentle luxury. With a calming environment, thoughtful service, and genuine warmth, the King & Queen bridal experience is designed to help you feel celebrated, inside and out.",
    },
    {
      image: "/assets/aboutimg/Product13.webp",
      title: "Premium Products",
      body: "We use premium products that nourish, protect, and perform. Every formula is carefully chosen to elevate your look. Expect results you can see, feel, and trust — because your skin deserves nothing less.",
    },
    {
      image: "/assets/aboutimg/Ambience6.webp",
      title: "Luxury Ambience",
      body: "Everything about our salon is designed to put you at ease. It's clean, calm, and comfortable. You will know you are in the right hands the moment you walk through our doors.",
    },
  ];

  const pillars = [
    { icon: "◈", title: "Our Vision", body: "To become a trusted beauty destination where every client feels confident, empowered, and radiant — setting new standards in hair, skincare, and artistry." },
    { icon: "◎", title: "Our Mission", body: "Deliver high-quality, personalized beauty services through skilled professionals, premium products, and a welcoming atmosphere that leaves every client feeling celebrated." },
    { icon: "✦", title: "Our Promise", body: "Every visit is a ritual — from the warm welcome to the final reveal. We promise attentive care, honest advice, and results that speak for themselves." },
  ];

  return (
    <div className="ab-root" ref={pageRef}>

      {/* ══════════════════════ HERO ══════════════════════ */}
      <section className="ab-hero">
        <div className="ab-hero-bg">
          {/* Hero image is above the fold — eager load */}
          <img
            src="/assets/imagess/model2.jpg"
            alt="salon"
            className="ab-hero-img"
            loading="eager"
            decoding="sync"
          />
          <div className="ab-hero-overlay" />
        </div>
        <div className="ab-hero-content">
          <p className="ab-hero-eyebrow ab-hero-sub">King & Queen · Since 2015</p>
          <div className="ab-hero-title">
            <div className="ab-clip"><span className="ab-hero-line">The Story</span></div>
            <div className="ab-clip"><span className="ab-hero-line ab-italic">Behind Us</span></div>
          </div>
          <p className="ab-hero-tagline ab-hero-sub">
            Where artistry, care, and elegance converge into one unforgettable experience.
          </p>
          <div className="ab-hero-cta">
            <button className="ab-btn-primary" onClick={() => navigate("/contact")}>Book a Slot</button>
            <button className="ab-btn-ghost" onClick={() => navigate("/services")}>Explore Services</button>
          </div>
        </div>
        <div className="ab-hero-scroll">
          <span>Scroll</span>
          <div className="ab-scroll-bar" />
        </div>
      </section>

      {/* ══════════════════════ IMAGE PAIR ══════════════════════ */}
      <div className="ab-image-pair">
        <div className="ab-image-pair-item yeahitem">
          <img
            src="/assets/about/abt.png"
            alt="salon model"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="ab-image-pair-item yeahitem1">
          <img
            src="/assets/serviceimages/Haircut2.png"
            alt="bridal"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      {/* ══════════════════════ STORY TEXT BLOCK ══════════════════════ */}
      <section className="ab-intro-block">
        <div className="ab-intro-inner">
          <p className="ab-eyebrow ab-reveal" style={{ textAlign: "center" }}>Welcome to King & Queen</p>
          <h2 className="ab-section-title ab-reveal" style={{ textAlign: "center" }}>
            You are the<br /><em>starting point</em>
          </h2>
          <p className="ab-intro-body ab-reveal">
            You have probably been to salons that left you feeling unseen, where your needs didn't quite matter.
            At King & Queen, that ends. Here, you are the starting point — your comfort, your preferences, your pace.
            Every consultation begins with real conversation, where your voice leads our way. Our stylists work with
            genuine care and attention, crafting styles meant to move with you and reflect your identity.
          </p>
          <div className="ab-intro-cta ab-reveal">
            <button className="ab-btn-primary" onClick={() => navigate("/contact")}>Contact Us</button>
          </div>
        </div>
      </section>

      {/* ══════════════════════ STATS STRIP ══════════════════════ */}
      <section className="ab-stats-strip">
        <div className="ab-stats-inner ab-stagger-parent">
          {stats.map((s, i) => (
            <div key={i} className="ab-stat-item ab-stagger-child">
              <span
                className="ab-stat-num"
                data-val={s.val}
                data-suffix={s.suffix}
              >
                {s.val}{s.suffix}
              </span>
              <span className="ab-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════ EXPERIENCE ══════════════════════ */}
      <section className="ab-next-level">
        <div className="ab-next-image ab-reveal">
          <img
            src="/assets/aboutimg/Model1.webp"
            alt="experience"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="ab-next-text">
          <p className="ab-eyebrow ab-reveal">Experience Next Level Care</p>
          <h2 className="ab-section-title ab-reveal">
            Building more than<br /><em>a salon</em>
          </h2>
          <p className="ab-body-text ab-reveal">
            We see beauty as a deeply personal experience. We are building more than a salon — we are curating a space
            where sophistication meets expertise. Our vision is to stand among the most trusted salon names in the region,
            not by following trends, but by setting exceptional standards and new benchmarks for every client we serve.
          </p>
          <button className="ab-btn-primary ab-reveal" onClick={() => navigate("/services")}>View Services</button>
        </div>
      </section>

      {/* ══════════════════════ FEATURE ROWS ══════════════════════ */}
      <section className="ab-features">
        {features.map((feat, i) => (
          <div key={i} className={`ab-feat-row ${i % 2 !== 0 ? "ab-feat-row-reverse" : ""}`}>
            <div className="ab-feat-img-wrap">
              <img
                src={feat.image}
                alt={feat.title}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="ab-feat-text">
              <h2 className="ab-feat-title">{feat.title}</h2>
              <p className="ab-feat-body">{feat.body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ══════════════════════ PILLARS ══════════════════════ */}
      <section className="ab-pillars">
        <div className="ab-pillars-header">
          <p className="ab-eyebrow ab-reveal" style={{ textAlign: "center" }}>Our Foundation</p>
          <h2 className="ab-section-title ab-reveal" style={{ textAlign: "center" }}>
            What drives <em>everything</em> we do
          </h2>
        </div>
        <div className="ab-pillars-grid ab-stagger-parent">
          {pillars.map((p, i) => (
            <div key={i} className="ab-pillar-card ab-stagger-child">
              <span className="ab-pillar-icon">{p.icon}</span>
              <h3 className="ab-pillar-title">{p.title}</h3>
              <p className="ab-pillar-body">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════ EXPERIENCE / VIDEO SECTION ══════════════════════ */}
      <section className="ab-experience">
        <div className="ab-exp-text">
          <p className="ab-eyebrow ab-reveal" style={{ color: "var(--ab-gold-light)" }}>Our Space</p>
          <h2 className="ab-section-title ab-reveal" style={{ color: "#fff" }}>
            Step inside<br /><em>our sanctuary</em>
          </h2>
          <p className="ab-exp-body ab-reveal">
            Experience ultimate relaxation in a space designed to calm the senses
            and elevate your spirit. Every corner of King &amp; Queen is crafted
            with you in mind.
          </p>
        </div>
        <div className="ab-exp-video ab-reveal">
          {/* preload="none" defers video loading until user interacts */}
          <video
            src="/assets/about/shop.mp4"
            controls
            playsInline
            preload="none"
            poster="/assets/about/aboutbg.png"
          />
          <div className="ab-exp-tags">
            <span>Luxury</span>
            <span>Relaxation</span>
            <span>Wellness</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════ GALLERY ══════════════════════ */}
      <section className="ab-gallery">
        <div className="ab-gallery-header ab-reveal">
          <p className="ab-eyebrow" style={{ textAlign: "center" }}>The Portfolio</p>
          <h2 className="ab-section-title" style={{ textAlign: "center" }}>
            Salon <em>Gallery</em>
          </h2>
        </div>

        <div className="ab-tabs">
          {Object.keys(galleryData).map((tab) => (
            <button
              key={tab}
              className={`ab-tab ${activeTab === tab ? "ab-tab-active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="ab-gallery-grid">
          {galleryData[activeTab].map((item, i) => (
            <div key={i} className="ab-gallery-card">
              {item.type === "image" ? (
                <>
                  <img
                    src={item.src}
                    alt={`gallery ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    onClick={() => setModalImage(item.src)}
                  />
                  <div className="ab-gallery-hover"><span>View</span></div>
                </>
              ) : (
                /* preload="none" defers video loading until user plays */
                <video controls preload="none">
                  <source src={item.src} type="video/mp4" />
                </video>
              )}
            </div>
          ))}
        </div>

        {modalImage && (
          <div className="ab-modal" onClick={() => setModalImage(null)}>
            <button className="ab-modal-close" onClick={() => setModalImage(null)}>✕</button>
            {/* Modal image is user-triggered — eager load for instant display */}
            <img
              src={modalImage}
              alt="Preview"
              loading="eager"
              decoding="async"
            />
          </div>
        )}
      </section>

      {/* ══════════════════════ CTA BANNER ══════════════════════ */}
      <section className="ab-cta-banner">
        <div className="ab-cta-banner-bg">
          <img
            src="/assets/aboutimg/Banner13.webp"
            alt="bridal"
            loading="lazy"
            decoding="async"
          />
          <div className="ab-cta-banner-overlay" />
        </div>
        <div className="ab-cta-banner-content ab-reveal">
          <p className="ab-eyebrow" style={{ color: "var(--ab-gold)" }}>Ready?</p>
          <h2 className="ab-section-title" style={{ color: "#fff" }}>
            An exceptional salon<br /><em>experience awaits you</em>
          </h2>
          <p className="ab-cta-body">
            Connect with us today and step into a salon experience that's uniquely crafted just for you.
          </p>
          <button className="ab-btn-gold" onClick={() => navigate("/contact")}>Contact Us</button>
        </div>
      </section>

    </div>
  );
};

export default About;